import { levels, messLevel } from "./rules.js";
import { dayKey, localMinutes, parseHm, weekdayOfKey } from "./time.js";
import type { ActivityAnimation, GameState } from "./types.js";

/** Idle behaviours the avatar does on its own. Ids match animation ids in packages/sprites. */
export type IdleBehavior =
  | "idle"
  | "look_around"
  | "window_look"
  | "scratch_head"
  | "walk"
  | "sit"
  | "nap"
  | "read"
  | "pushups"
  | "squats"
  | "pullup"
  | "dumbbells"
  | "laptop"
  | "write"
  | "sweep"
  | "vacuum"
  | "cook"
  | "play_console"
  | "guitar";

export interface BehaviorPick {
  behavior: IdleBehavior;
  durationMs: number;
}

/** Map an activity animation (LLM enum) to the concrete animation the avatar plays. */
export function activityToAnimation(anim: ActivityAnimation, owned: ReadonlySet<string>): string {
  switch (anim) {
    case "train":
      return owned.has("dumbbells") ? "dumbbells" : "pushups";
    case "read":
      return "read";
    case "work":
      return "laptop";
    case "write":
      return "write";
    case "trash":
      return "trash_out";
    case "clean":
      return owned.has("vacuum") ? "vacuum" : "sweep";
    case "cook":
      return "cook";
    case "shop":
      return "shop_return";
    // Meeting people happens away from home: the director shows the café, the bar or the park.
    case "coffee":
      return "visit_cafe";
    case "beer":
      return "visit_bar";
    case "walk":
      return "visit_park";
    case "other":
      return "nod";
  }
}

/** Is it night for the scene (sleep animation, dimming)? */
export function isNight(state: GameState, now: number): boolean {
  const m = localMinutes(now, state.settings.timezone);
  const from = parseHm(state.settings.nightFrom) ?? 23 * 60;
  const until = parseHm(state.settings.nightUntil) ?? 6 * 60 + 30;
  return from <= until ? m >= from && m < until : m >= from || m < until;
}

/**
 * Should the companion be in bed? Night, or an hour after the active hours end (whichever is earlier),
 * until the night ends. The screen still dims only at night (isNight).
 */
export function isBedtime(state: GameState, now: number): boolean {
  if (isNight(state, now)) return true;
  const m = localMinutes(now, state.settings.timezone);
  const activeUntil = parseHm(state.settings.activeUntil);
  const nightFrom = parseHm(state.settings.nightFrom) ?? 23 * 60;
  // Only an evening end of activity moves bedtime earlier (a 3:00 setting would mean the whole day).
  if (activeUntil === null || activeUntil < 18 * 60) return false;
  const bed = activeUntil + 60;
  return bed < nightFrom && m >= bed && m < nightFrom;
}

/** The companion's working hours (Monday–Friday, normal mode): he is in the office. */
export function isWorkTime(state: GameState, now: number): boolean {
  const s = state.settings;
  if (!s.workOn || state.mode.kind !== "normal") return false;
  const day = weekdayOfKey(dayKey(now, s.timezone));
  if (day === 0 || day === 6) return false;
  const m = localMinutes(now, s.timezone);
  const from = parseHm(s.workFrom) ?? 9 * 60;
  const until = parseHm(s.workUntil) ?? 17 * 60;
  return from < until && m >= from && m < until;
}

/** Daytime outing the avatar may take on its own (walk or shopping, leaves through the door). */
export interface OutingPick {
  kind: "walk_out" | "shopping_out";
  durationMs: number;
}

const MAX_OUTINGS_PER_DAY = 2;
/** Per idle pick (one every ~10-30 s): roughly one outing per few quiet hours. */
const OUTING_CHANCE = 0.004;

/**
 * Maybe go out for a while: only in active hours, in normal mode, not muted, when nobody
 * talked to the companion for 20 minutes, at most twice a day. Any interaction brings him back.
 */
export function pickOuting(
  state: GameState,
  now: number,
  quietForMs: number,
  outingsToday: number,
  random: () => number,
): OutingPick | null {
  if (outingsToday >= MAX_OUTINGS_PER_DAY || quietForMs < 20 * 60_000) return null;
  if (state.mode.kind !== "normal" || (state.mute && now < state.mute.until)) return null;
  const m = localMinutes(now, state.settings.timezone);
  const from = parseHm(state.settings.activeFrom) ?? 10 * 60;
  const until = parseHm(state.settings.activeUntil) ?? 21 * 60;
  if (!(m >= from && m < until - 45)) return null;
  if (random() >= OUTING_CHANCE) return null;
  const kind = random() < 0.6 ? "walk_out" : "shopping_out";
  return { kind, durationMs: (20 + Math.floor(random() * 25)) * 60_000 };
}

/**
 * Weighted pick of the next idle behaviour, never the same twice in a row.
 * Weights follow progress: a growing Mind level means more reading, a pull-up bar means pull-ups.
 */
export function pickIdleBehavior(
  state: GameState,
  now: number,
  previous: IdleBehavior | null,
  random: () => number,
): BehaviorPick {
  const owned = new Set(state.inventory.map((i) => i.id));
  const lv = levels(state.stats.xp);
  const mode = state.mode.kind;
  const muted = state.mute && now < state.mute.until;
  const weights: [IdleBehavior, number][] = [
    ["idle", 3],
    ["look_around", 3],
    ["window_look", 2],
    ["scratch_head", 1],
    ["walk", 3],
    ["sit", 2],
    ["nap", mode === "sick" ? 4 : 0.7],
    ["read", 1 + lv.mind * 0.8 + (muted ? 3 : 0)],
    ["pushups", mode === "sick" ? 0 : 0.5 + lv.body * 0.4],
    ["squats", mode === "sick" ? 0 : 0.3 + lv.body * 0.3],
    ["write", 0.5 + lv.work * 0.3],
    ["sweep", messLevel(state, now) > 0 ? 0 : 0.4 + lv.home * 0.3],
    ["cook", 0.6],
  ];
  if (owned.has("pullup_bar") && mode !== "sick") weights.push(["pullup", 0.6 + lv.body * 0.3]);
  if (owned.has("dumbbells") && mode !== "sick") weights.push(["dumbbells", 0.6 + lv.body * 0.3]);
  if (owned.has("laptop") || owned.has("desk")) weights.push(["laptop", 0.8 + lv.work * 0.5]);
  if (owned.has("vacuum")) weights.push(["vacuum", 0.5]);
  if (owned.has("console")) weights.push(["play_console", 1.2]);
  if (owned.has("guitar")) weights.push(["guitar", 1]);

  const pool = weights.filter(([b, w]) => w > 0 && b !== previous);
  const total = pool.reduce((s, [, w]) => s + w, 0);
  let r = random() * total;
  let chosen: IdleBehavior = pool[0]?.[0] ?? "idle";
  for (const [b, w] of pool) {
    r -= w;
    if (r <= 0) {
      chosen = b;
      break;
    }
  }
  const base = chosen === "walk" || chosen === "look_around" ? 6000 : chosen === "nap" ? 30000 : 15000;
  return { behavior: chosen, durationMs: base + Math.floor(random() * base) };
}

/** Day key helper re-exported for clients that only import behaviour. */
export const todayKey = (state: GameState, now: number) => dayKey(now, state.settings.timezone);
